import { useCallback, useEffect, useMemo } from 'react'
import CSPDropDown from '../common/CommonFieldWidgets/CSPDropDown'
import { useFormContext } from 'react-hook-form'
import { updateStateProps } from '../../actions'
import { APP_ID_LOGGING_SERVICE, APP_ID_SDWAN, CSP_FIELD_NAME } from '../common/GlobalConstants'
import { FED_HIGH_REGION, FED_MOD_REGION } from '../../constants'
import { ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP, ACTIVATION_UI_ERROR_CODES } from '../../ErrorCodes'
import { FF_SDWAN_CSP_BYPASS } from '../../../common/utils/FeatureFlags'
import { getVarValue } from '../../../common/utils/Sparky'

import type { VFC } from 'react'
import type { IDropDownData } from '../common/DropDown/DropDown'
import type { CommonStoreState } from '../../store'
import type { Actions } from '../../actions'
import type { SupportAccount } from '@sparky/framework'
import type { BannerProps } from './UAccordion'

export type UCSPComponentProp = {
  state: CommonStoreState
  actions: Actions
  updateTitle: (title: string) => void
  setAccordionOpen: (isOpen: boolean) => void
  setEditDisable: (disable: boolean) => void
  setBanner: (props: BannerProps) => void
}

const UCSPComponent: VFC<UCSPComponentProp> = (props) => {
  const enableSdwanBypass = getVarValue(FF_SDWAN_CSP_BYPASS, false)
  const { state, updateTitle, setAccordionOpen, setEditDisable, setBanner } = props
  const { instances, lockedInfo, manifest, loading } = state
  const { watch } = useFormContext()
  const currentCSP = watch(CSP_FIELD_NAME)

  /**
   * Skip all instances has to be same csp if activating sdwan
   */
  const primaryAppId = useMemo<string>(() => {
    if (!manifest) {
      return ''
    }
    return manifest.primary_app_id
  }, [manifest])

  const supportAccounts = useMemo(() => {
    let filteredSupportAccounts: SupportAccount[]
    // filtering for Fedramp
    // categoryId: 1 - Commercial, 2 - Fedramp Moderate, 3 - Fedramp High
    const isFedModRegionLock = lockedInfo?.regions?.some(region => region === FED_MOD_REGION)
    const isFedHighRegionLock = lockedInfo?.regions?.some(region => region === FED_HIGH_REGION)

    if (!isFedModRegionLock && !isFedHighRegionLock) {
      // if region not fed high or mod, only display commercial accounts
      filteredSupportAccounts = state.supportAccounts.filter(each => each.supportAccountCategory[0].categoryId === 1)
    }
    else {
      // filter supportAccounts based on locked region
      filteredSupportAccounts = state.supportAccounts.filter(each => {
        // it should not contain both high and mod
        if (isFedModRegionLock) {
          return each.supportAccountCategory[0].categoryId === 2
        }
        if (isFedHighRegionLock) {
          return each.supportAccountCategory[0].categoryId === 3
        }
        return false
      })
    }
    // filtering for lockedInfo
    // use lockedInfo.support_account_ids since we support array
    let lockSupportAccountIdSet: Set<string>
    if (lockedInfo?.support_account_ids) {
      lockSupportAccountIdSet = new Set(lockedInfo?.support_account_ids)
      filteredSupportAccounts = filteredSupportAccounts.filter((each: any) => lockSupportAccountIdSet.has(String(each.accountid)))
    }
    return filteredSupportAccounts
  }, [lockedInfo?.regions, lockedInfo?.support_account_ids, state.supportAccounts])

  const onCspChange = useCallback((value: IDropDownData) => {
    setAccordionOpen(false)
  }, [setAccordionOpen])

  useEffect(() => {
    if (loading) {
      return
    }
    if (currentCSP === '') {
      // empty csp, need to warn
      updateTitle('Customer Support Account')
      setBanner({ display: true, message: ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.NO_ACCESS_TO_CSP], appearance: 'error' })
    }
    else if (currentCSP) {
      updateStateProps({ supportAccountId: String(currentCSP.id) })
      updateTitle(`Customer Support Account: ${currentCSP.value}`)
    }
  }, [currentCSP, setBanner, updateTitle, loading])

  // useEffect(() => {
  //   if (!instances.length) {
  //     return
  //   }
  //   const supportAccountName = instances[0].support_account_name ?? instances.find(each => each.support_account_name)
  //   updateTitle(`Customer Support Account: ${supportAccountName}`)
  // }, [instances, updateTitle])

  const cspDisabled = useMemo(() => {
    // disable only if set by lockedInfo, if user only have 1 support account, don't disable and let user choose
    return Boolean(lockedInfo?.support_account_ids?.length === 1)
  }, [lockedInfo?.support_account_ids?.length])

  const cspProps = useMemo(() => {
    const callbacks = {
      changeHandlerCallback: onCspChange,
    }
    // APPORTAL-10294, csp match mechanism doesn't apply to SDWAN, so need to exclude it also when finding instance
    const curTsgInstCsp = (enableSdwanBypass && primaryAppId === APP_ID_SDWAN) ?
      undefined :
      instances.get(APP_ID_LOGGING_SERVICE)?.support_account_id || instances.find(each => each.app_id !== APP_ID_SDWAN)?.support_account_id
    return {
      callbacks,
      supportAccounts,
      isLoading: false,
      disable: cspDisabled,
      curTsgInstCsp,
      currentTsg: state.tsg?.id ?? '',
      tsg: state.tsg,
      lockedInfo: lockedInfo,
    }
  }, [onCspChange, primaryAppId, instances, supportAccounts, cspDisabled, state.tsg, lockedInfo, enableSdwanBypass])

  useEffect(() => {
    if (cspDisabled) {
      setEditDisable(true)
      setAccordionOpen(false)
    }
    else {
      setEditDisable(false)
    }
  }, [cspDisabled, setAccordionOpen, setEditDisable])

  return (
    <div>
      <CSPDropDown {...cspProps} />
    </div>
  )

}

export default UCSPComponent
